import React from "react";
import PropTypes from "prop-types"; // Import PropTypes for prop validation
import "./Skeleton.scss";

/**
 * Skeleton Component
 * 
 * A reusable component that displays a skeleton loader with customizable animations, shapes, 
 * dimensions, and styles. Useful as a placeholder while loading data.
 * 
 * @param {Object} props - The props for the Skeleton component.
 * @param {"shimmer" | "pulse" | "sliding"} props.animation - The animation type for the skeleton. Defaults to "shimmer".
 * @param {"rectangle" | "circle"} props.shape - The shape of the skeleton. Defaults to "rectangle".
 * @param {string | number} props.width - The width of the skeleton (e.g., "100%", "200px", or a number). Defaults to "100%".
 * @param {string | number} props.height - The height of the skeleton (e.g., "16px", or a number). Defaults to "16px".
 * @param {Object} props.style - Additional inline styles to apply to the skeleton. Defaults to an empty object.
 * @param {string} props.className - Additional class names to apply to the skeleton for custom styling. Defaults to an empty string.
 * 
 * @returns {JSX.Element} A skeleton loader component.
 */
const Skeleton = ({
  animation = "shimmer",
  shape = "rectangle",
  width = "100%",
  height = "16px",
  style = {},
  className = "",
}) => {
  // Determine the animation class based on the animation prop
  const animationClass =
    animation === "shimmer"
      ? "skeleton-shimmer"
      : animation === "pulse"
      ? "skeleton-pulse"
      : animation === "sliding"
      ? "skeleton-sliding"
      : "";

  // Determine the shape class based on the shape prop
  const shapeClass = shape === "circle" ? "skeleton-circle" : "skeleton-rectangle";

  // Combine all class names
  const combinedClassName = `${animationClass} ${shapeClass} ${className}`.trim();

  return (
    <div
      className={`skeleton ${combinedClassName}`}
      style={{ width, height, ...style }} // Inline styles for dynamic width, height, and additional custom styles
    ></div>
  );
};

// PropTypes for type-checking and documentation
Skeleton.propTypes = {
  animation: PropTypes.oneOf(["shimmer", "pulse", "sliding"]), //The animation type for the skeleton. Options are "shimmer", "pulse", or "sliding".

  shape: PropTypes.oneOf(["rectangle", "circle"]), //The shape of the skeleton. Options are "rectangle" or "circle".

  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), //The width of the skeleton. Can be a string (e.g., "100%", "200px") or a number (e.g., 100).

  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), //The height of the skeleton. Can be a string (e.g., "16px") or a number (e.g., 50).

  style: PropTypes.object, //Inline styles for the skeleton. Useful for additional customizations.

  className: PropTypes.string, //Additional class names to apply to the skeleton for custom styling.
};

// Default Props to ensure defaults are set when props are not provided
Skeleton.defaultProps = {
  animation: "shimmer",
  shape: "rectangle",
  width: "100%",
  height: "16px",
  style: {},
  className: "",
};

export default Skeleton;
